import { useNavigate } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import { ROUTES } from '@/config/routes';
import { useModalStore } from '@/store/use-modal-store';
import { DiscountGroup, useCapitalStore } from '@/store/use-capital-store';
import { Dialog, DialogContent } from '@/components/ui';
import { Button } from '@/components/ui/button';
import DottedTexture from '@/assets/branding/dotted-texture.svg?react';

export default function DiscountsModal() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const isOpen = useModalStore((state) => state.isDiscountsModalOpen);
  const { discountGroups, appliedDiscountGroup, setAppliedDiscountGroup } =
    useCapitalStore(
      useShallow((state) => ({
        discountGroups: state.discountGroups,
        appliedDiscountGroup: state.appliedDiscountGroup,
        setAppliedDiscountGroup: state.setAppliedDiscountGroup,
      })),
    );

  const handleUseDiscount = (group: DiscountGroup) => {
    setAppliedDiscountGroup(group);

    useModalStore.setState({ isDiscountsModalOpen: false });
    navigate(`${ROUTES.VEST_CAPITAL}#capital-plans`);
    toast.success(
      `${t('discountsModal.discountActive')}! ${t('discountsModal.discountActiveDescription')}`,
    );
  };

  const handleCancelDiscount = () => {
    setAppliedDiscountGroup(null);
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) =>
        useModalStore.setState({ isDiscountsModalOpen: open })
      }
    >
      <DialogContent className="gap-10 overflow-hidden border-none bg-card sm:max-w-[440px] sm:!rounded-3xl">
        <div className="relative flex flex-col gap-8 p-5 !pb-0 pt-8 sm:p-14 sm:pt-20">
          <DottedTexture className="pointer-events-none absolute top-0 z-[-1] h-auto w-full scale-[1.7] opacity-50 [&_ellipse]:fill-[#65B482]" />

          <div className="text-center text-4xl font-semibold capitalize leading-[130%]">
            {t('discountsModal.yourDiscounts')}
          </div>

          <div className="no-scrollbar flex max-h-[378px] flex-col gap-1 overflow-y-auto pb-5 sm:pb-14">
            {discountGroups.map((group) => {
              const isApplied = appliedDiscountGroup?.codeId === group.codeId;

              return (
                <div
                  key={group.codeId}
                  className="flex items-center justify-between gap-8 rounded-xl bg-background px-4 py-3"
                >
                  <div className="text-lg font-medium">
                    {group.discountPct}% {t('discountsModal.discount')} (
                    {group.planTitles.join(', ')})
                  </div>

                  <Button
                    variant={isApplied ? 'outline2' : 'contrast'}
                    size="lg"
                    className="capitalize"
                    onClick={() =>
                      isApplied
                        ? handleCancelDiscount()
                        : handleUseDiscount(group)
                    }
                  >
                    {isApplied
                      ? t('discountsModal.cancel')
                      : t('discountsModal.useNow')}
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
